class TabsCustom extends HTMLElement {
  #controller = new AbortController();

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #tabTriggers() {
    return Array.from(this.querySelectorAll('[data-tab-trigger]')).filter((element) => element instanceof HTMLButtonElement);
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #tabPanels() {
    return Array.from(this.querySelectorAll('[data-tab-panel]')).filter((element) => element instanceof HTMLElement);
  }

  #syncTriggerLabels() {
    const panels = this.#tabPanels;
    const triggers = this.#tabTriggers;

    panels.forEach((panel) => {
      const heading = panel.dataset.tabHeading;
      if (!heading) return;

      const trigger = triggers.find((t) => t.dataset.tabId === panel.dataset.tabId);
      if (trigger) trigger.textContent = heading;
    });
  }

  connectedCallback() {
    const { signal } = this.#controller;
    this.#syncTriggerLabels();

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#activateInitialTab();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #activateInitialTab() {
    const initialTabId = this.dataset.activeTabId;
    const fallbackTabId = this.#tabTriggers[0]?.dataset.tabId;

    this.activateTab(initialTabId || fallbackTabId);
  }

  /**
   * @param {string | undefined} tabId
   * @param {{ focusTrigger?: boolean }} options
   */
  activateTab(tabId, { focusTrigger = false } = {}) {
    if (!tabId) return;

    const triggers = this.#tabTriggers;
    const panels = this.#tabPanels;

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.tabId === tabId;

      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.tabIndex = isActive ? 0 : -1;

      if (isActive && focusTrigger) {
        trigger.focus();
      }
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.tabId === tabId;

      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.dataset.activeTabId = tabId;
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-tab-trigger]') : null;

    if (!(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) return;

    this.activateTab(trigger.dataset.tabId);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || !event.target.matches('[data-tab-trigger]')) return;

    const currentTrigger = event.target;
    const triggers = this.#tabTriggers;
    const currentIndex = triggers.indexOf(currentTrigger);

    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % triggers.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = triggers.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTrigger = triggers[nextIndex];
    this.activateTab(nextTrigger.dataset.tabId, { focusTrigger: true });
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const tabPanel = event.target.closest('[data-tab-panel]');

    if (!(tabPanel instanceof HTMLElement) || !this.contains(tabPanel)) return;

    this.activateTab(tabPanel.dataset.tabId);
  };
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
